import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Edit,
  FileText,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import {
  DrugLicense,
  drugLicenseService,
} from "../../services/drug-license.service";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuthStore } from "../../stores/authStore";

export default function DrugLicensesPage() {
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("compliance:manage");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    licenseNumber: string;
  }>({ isOpen: false, id: "", licenseNumber: "" });
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["drug-licenses", { search, page, selectedStoreId }],
    queryFn: () =>
      drugLicenseService.getAll({
        search,
        page,
        limit: 20,
        storeId: selectedStoreId ?? undefined,
      }),
    enabled: Boolean(selectedStoreId),
  });

  const { data: expiringLicenses } = useQuery({
    queryKey: ["drug-licenses", "expiring", selectedStoreId],
    queryFn: () =>
      drugLicenseService.getExpiring(30, selectedStoreId ?? undefined),
    enabled: Boolean(selectedStoreId),
  });

  const deleteMutation = useMutation({
    mutationFn: drugLicenseService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drug-licenses"] });
      setDeleteModal({ isOpen: false, id: "", licenseNumber: "" });
    },
  });

  const handleDeleteClick = (id: string, licenseNumber: string) => {
    setDeleteModal({ isOpen: true, id, licenseNumber });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(deleteModal.id);
  };

  const formatDate = (dateString: string) => {
    const dateOnly = dateString.slice(0, 10);
    return new Date(`${dateOnly}T00:00:00`).toLocaleDateString();
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const [year, month, day] = expiryDate.slice(0, 10).split("-").map(Number);
    const now = new Date();
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round(
      (Date.UTC(year, month - 1, day) - today) / (1000 * 60 * 60 * 24),
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Drug Licenses
        </h1>
        {canManage && (
          <Link
            to="/drug-licenses/create"
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add License
          </Link>
        )}
      </div>

      {expiringLicenses && expiringLicenses.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
            <AlertTriangle className="h-5 w-5" />
            Licenses Expiring Soon ({expiringLicenses.length})
          </div>
          <div className="space-y-1 text-sm text-yellow-700">
            {expiringLicenses.slice(0, 3).map((license: DrugLicense) => (
              <div key={license.id}>
                {license.licenseNumber} - {license.licenseType} expires{" "}
                {getDaysUntilExpiry(license.expiryDate) === 0
                  ? "today"
                  : `in ${getDaysUntilExpiry(license.expiryDate)} days`}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search licenses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : isError ? (
          <div className="p-8 text-center text-red-500">
            <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
            {(error as any)?.response?.data?.message ||
              "Failed to load drug licenses. Please check your permissions and try again."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    License Number
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Store
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Expiry Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Pharmacist
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                  {canManage && (
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((license: DrugLicense) => {
                  const daysUntilExpiry = getDaysUntilExpiry(
                    license.expiryDate,
                  );
                  return (
                    <tr key={license.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">
                        {license.licenseNumber}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {license.licenseType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {license.store?.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div>{formatDate(license.expiryDate)}</div>
                        {daysUntilExpiry <= 30 && daysUntilExpiry >= 0 && (
                          <div className="text-xs text-yellow-600">
                            {daysUntilExpiry === 0
                              ? "Expires today"
                              : `${daysUntilExpiry} days left`}
                          </div>
                        )}
                        {daysUntilExpiry < 0 && (
                          <div className="text-xs text-red-600">Expired</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {license.registeredPharmacist || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            license.isActive && daysUntilExpiry >= 0
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {daysUntilExpiry < 0
                            ? "Expired"
                            : license.isActive
                              ? "Active"
                              : license.status.replace("_", " ")}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/drug-licenses/${license.id}/edit`}
                              className="p-1 text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={() =>
                                handleDeleteClick(
                                  license.id,
                                  license.licenseNumber,
                                )
                              }
                              className="p-1 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to{" "}
              {Math.min(page * 20, data.pagination.total)} of{" "}
              {data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(data.pagination.totalPages, p + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {canManage && (
        <ConfirmDialog
          isOpen={deleteModal.isOpen}
          onClose={() =>
            setDeleteModal({ isOpen: false, id: "", licenseNumber: "" })
          }
          onConfirm={handleDeleteConfirm}
          title="Delete Drug License"
          message={`Are you sure you want to delete license "${deleteModal.licenseNumber}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
